/*
 * Copyright (C) GMS Inc - All Rights Reserved
 * This file is part of GMSCloud. - Proprietary and confidential
 * Unauthorized copying of this file, via any medium is strictly prohibited
 *
 * Created on   : 18-Jul-2026
 * @author      : Vincy R
 * Requirement# : 22300
 * Purpose      : Shared per-template mini card + pills — a published version and its draft (if any)
 *                render as two SEPARATE cards (same pattern on the Provider grid and the By-tenant view),
 *                never merged into one card with both statuses.
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';

/** Small pill showing the published version label (e.g. "v2"). */
export function VersionPill({ v }: { v: string }) {
  return (
    <Box component="span" sx={{ fontSize: 11, fontWeight: 700, color: 'success.dark', whiteSpace: 'nowrap',
      bgcolor: (theme) => alpha(theme.palette.success.main, 0.14), px: 0.85, py: 0.25, borderRadius: 999 }}>{v}</Box>
  );
}

/** Small grey pill showing a task count. */
export function CountPill({ n }: { n: number }) {
  return (
    <Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', bgcolor: 'grey.100', px: 1, py: 0.25, borderRadius: 999, whiteSpace: 'nowrap' }}>
      {`${n} ${n === 1 ? 'task' : 'tasks'}`}
    </Box>
  );
}

/** One template card (published OR draft) for a trade; the whole card opens that specific version. */
export default function TemplateMiniCard({ initial, name, category, softBg, ink, draft, onClick, children }: {
  initial: string; name: string; category: string; softBg: string; ink: string; draft?: boolean;
  onClick: () => void; children: React.ReactNode;
}) {
  return (
    <Box onClick={onClick}
      sx={{ bgcolor: draft ? (theme => alpha(theme.palette.warning.main, 0.05)) : 'background.paper',
        border: 1, borderColor: draft ? (theme => alpha(theme.palette.warning.main, 0.5)) : 'divider',
        borderRadius: 2.5, p: 1.5, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 1.25,
        transition: 'all .12s', '&:hover': { borderColor: draft ? 'warning.main' : ink, boxShadow: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ width: 30, height: 30, borderRadius: 1.5, bgcolor: softBg, color: ink, flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 13 }}>{initial}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography noWrap fontWeight={700} fontSize={13.5}>{name}</Typography>
          <Typography noWrap color="text.secondary" fontSize={11}>{category}</Typography>
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minHeight: 22, flexWrap: 'wrap' }}>{children}</Box>
    </Box>
  );
}
